/**
 * CharacterGenerationCard - A standalone card component that displays character
 * generation progress with visual states for analyzing and generating stages.
 */
export class CharacterGenerationCard {
  static STATES = {
    IDLE: 'idle',
    ANALYZING: 'analyzing',
    GENERATING_2D: 'generating_2d',
    COMPLETE: 'complete',
    ERROR: 'error'
  };

  static STYLES_INJECTED = false;

  constructor(options = {}) {
    this.apiBaseUrl = options.apiBaseUrl || 'https://ajcb6tx6g3.eu-central-1.awsapprunner.com';
    this.pollInterval = options.pollInterval || 2000;
    this.onComplete = options.onComplete || (() => {});
    this.onError = options.onError || (() => {});
    this.onStageChange = options.onStageChange || (() => {});

    this.state = CharacterGenerationCard.STATES.IDLE;
    this.generationId = null;
    this.pollTimer = null;
    this.imageUrl = null;
    this.result = null;
    this.errorMessage = null;

    this._injectStyles();
    this._buildElement();
    this._updateUI();
  }

  _injectStyles() {
    if (CharacterGenerationCard.STYLES_INJECTED) return;
    CharacterGenerationCard.STYLES_INJECTED = true;

    const style = document.createElement('style');
    style.textContent = `
      @keyframes cgc-pulse {
        0%, 100% { box-shadow: 0 0 8px rgba(47, 208, 100, 0.4); }
        50% { box-shadow: 0 0 16px rgba(47, 208, 100, 0.8); }
      }

      @keyframes cgc-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      @keyframes cgc-progress {
        0% { background-position: 0% 50%; }
        100% { background-position: 100% 50%; }
      }

      @keyframes cgc-fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .cgc-card {
        width: 220px;
        min-height: 360px;
        background: rgba(20, 20, 25, 0.9);
        border-radius: 12px;
        border: 2px solid rgba(255, 255, 255, 0.1);
        padding: 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        font-family: system-ui, -apple-system, sans-serif;
        box-sizing: border-box;
        transition: border-color 0.3s ease, box-shadow 0.3s ease;
      }

      .cgc-card--analyzing {
        border-color: rgba(47, 208, 100, 0.5);
        animation: cgc-pulse 2s ease-in-out infinite;
      }

      .cgc-card--generating {
        border-color: rgba(47, 208, 100, 0.6);
      }

      .cgc-card--complete {
        border-color: #2fd064;
        box-shadow: 0 0 20px rgba(47, 208, 100, 0.5);
      }

      .cgc-card--error {
        border-color: #d04040;
        box-shadow: 0 0 12px rgba(208, 64, 64, 0.4);
      }

      .cgc-image-container {
        width: 180px;
        height: 270px;
        background: rgba(30, 30, 35, 0.8);
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        position: relative;
      }

      .cgc-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 8px;
        animation: cgc-fade-in 0.5s ease-out;
      }

      .cgc-placeholder {
        color: rgba(255, 255, 255, 0.4);
        font-size: 13px;
        text-align: center;
        padding: 20px;
      }

      .cgc-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid rgba(47, 208, 100, 0.2);
        border-top-color: #2fd064;
        border-radius: 50%;
        animation: cgc-spin 1s linear infinite;
      }

      .cgc-progress-container {
        width: 100%;
        height: 6px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 3px;
        overflow: hidden;
      }

      .cgc-progress-bar {
        height: 100%;
        background: linear-gradient(90deg, #2fd064, #40e080, #2fd064);
        background-size: 200% 100%;
        animation: cgc-progress 1.5s ease-in-out infinite;
        border-radius: 3px;
        transition: width 0.3s ease;
      }

      .cgc-status {
        color: rgba(255, 255, 255, 0.8);
        font-size: 13px;
        text-align: center;
        min-height: 20px;
      }

      .cgc-status--error {
        color: #f06060;
      }

      .cgc-status--success {
        color: #2fd064;
      }
    `;
    document.head.appendChild(style);
  }

  _buildElement() {
    this.element = document.createElement('div');
    this.element.className = 'cgc-card';

    this.imageContainer = document.createElement('div');
    this.imageContainer.className = 'cgc-image-container';

    this.imageEl = document.createElement('img');
    this.imageEl.className = 'cgc-image';
    this.imageEl.style.display = 'none';

    this.placeholder = document.createElement('div');
    this.placeholder.className = 'cgc-placeholder';
    this.placeholder.textContent = 'Ready to generate';

    this.spinner = document.createElement('div');
    this.spinner.className = 'cgc-spinner';
    this.spinner.style.display = 'none';

    this.imageContainer.appendChild(this.imageEl);
    this.imageContainer.appendChild(this.placeholder);
    this.imageContainer.appendChild(this.spinner);

    this.progressContainer = document.createElement('div');
    this.progressContainer.className = 'cgc-progress-container';
    this.progressContainer.style.display = 'none';

    this.progressBar = document.createElement('div');
    this.progressBar.className = 'cgc-progress-bar';
    this.progressBar.style.width = '0%';
    this.progressContainer.appendChild(this.progressBar);

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'cgc-status';

    this.element.appendChild(this.imageContainer);
    this.element.appendChild(this.progressContainer);
    this.element.appendChild(this.statusEl);
  }

  _updateUI() {
    const { STATES } = CharacterGenerationCard;

    // Reset card classes
    this.element.className = 'cgc-card';

    // Reset visibility
    this.imageEl.style.display = 'none';
    this.placeholder.style.display = 'none';
    this.spinner.style.display = 'none';
    this.progressContainer.style.display = 'none';

    // Reset status classes
    this.statusEl.className = 'cgc-status';

    switch (this.state) {
      case STATES.IDLE:
        this.placeholder.style.display = 'block';
        this.placeholder.textContent = 'Ready to generate';
        this.statusEl.textContent = '';
        break;

      case STATES.ANALYZING:
        this.element.classList.add('cgc-card--analyzing');
        this.spinner.style.display = 'block';
        this.progressContainer.style.display = 'block';
        this.progressBar.style.width = '30%';
        this.statusEl.textContent = 'Analyzing images...';
        break;

      case STATES.GENERATING_2D:
        this.element.classList.add('cgc-card--generating');
        if (this.imageUrl) {
          this.imageEl.src = this.imageUrl;
          this.imageEl.style.display = 'block';
        } else {
          this.spinner.style.display = 'block';
        }
        this.progressContainer.style.display = 'block';
        this.progressBar.style.width = '70%';
        this.statusEl.textContent = 'Generating character...';
        break;

      case STATES.COMPLETE:
        this.element.classList.add('cgc-card--complete');
        if (this.imageUrl) {
          this.imageEl.src = this.imageUrl;
          this.imageEl.style.display = 'block';
        }
        this.progressContainer.style.display = 'block';
        this.progressBar.style.width = '100%';
        this.statusEl.textContent = 'Generation complete';
        this.statusEl.classList.add('cgc-status--success');
        break;

      case STATES.ERROR:
        this.element.classList.add('cgc-card--error');
        this.placeholder.style.display = 'block';
        this.placeholder.textContent = 'Generation failed';
        this.statusEl.textContent = this.errorMessage || 'An error occurred';
        this.statusEl.classList.add('cgc-status--error');
        break;
    }
  }

  _setState(newState) {
    if (this.state !== newState) {
      this.state = newState;
      this._updateUI();
      this.onStageChange(newState);
    }
  }

  async startGeneration(imageFiles) {
    if (!imageFiles || imageFiles.length === 0) {
      this._setError('No images provided');
      return;
    }

    this._setState(CharacterGenerationCard.STATES.ANALYZING);
    this.imageUrl = null;
    this.result = null;
    this.errorMessage = null;

    try {
      const base64Images = await this._convertFilesToBase64(imageFiles);

      // Step 1: Analyze images to get descriptions
      const analyzeResponse = await fetch(`${this.apiBaseUrl}/api/analyze-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images: base64Images })
      });

      if (!analyzeResponse.ok) {
        const errorData = await analyzeResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `Analysis failed: HTTP ${analyzeResponse.status}`);
      }

      const analyzeData = await analyzeResponse.json();
      const descriptions = analyzeData.descriptions;

      if (!descriptions || descriptions.length === 0) {
        throw new Error('No descriptions returned from analysis');
      }

      // Step 2: Generate character from descriptions
      this._setState(CharacterGenerationCard.STATES.GENERATING_2D);

      const generateResponse = await fetch(`${this.apiBaseUrl}/api/generate-character`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ descriptions })
      });

      if (!generateResponse.ok) {
        const errorData = await generateResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `Generation failed: HTTP ${generateResponse.status}`);
      }

      const generateData = await generateResponse.json();
      this.generationId = generateData.id;
      this.imageUrl = generateData.imageUrl;
      this.result = {
        imageUrl: generateData.imageUrl,
        prompt: generateData.prompt,
        id: generateData.id,
        descriptions
      };

      this._setState(CharacterGenerationCard.STATES.COMPLETE);
      this.onComplete(this.result);
    } catch (error) {
      this._setError(error.message);
    }
  }

  async _convertFilesToBase64(files) {
    const promises = Array.from(files).map(file => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Send full data URL as expected by API
          resolve(reader.result);
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
    });
    return Promise.all(promises);
  }

  _startPolling() {
    this._stopPolling();
    this.pollTimer = setInterval(() => this._pollStatus(), this.pollInterval);
    this._pollStatus();
  }

  _stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  async _pollStatus() {
    if (!this.generationId) return;

    try {
      const response = await fetch(
        `${this.apiBaseUrl}/api/pipeline/status/${this.generationId}`
      );

      if (!response.ok) {
        throw new Error(`Status check failed: HTTP ${response.status}`);
      }

      const data = await response.json();
      const stage = data.stage || data.status;
      const imageUrl = data.image_url || data.imageUrl || data.result?.image_url;

      if (stage === 'failed' || stage === 'error') {
        this._stopPolling();
        this._setError(data.error || data.message || 'Generation failed');
        return;
      }

      if (stage === 'analyzing' || stage === 'analysis') {
        this._setState(CharacterGenerationCard.STATES.ANALYZING);
      } else if (stage === 'generating_2d' || stage === 'generating') {
        this._setState(CharacterGenerationCard.STATES.GENERATING_2D);
        if (imageUrl) {
          this.imageUrl = imageUrl;
          this._updateUI();
        }
      }

      // Check for completion conditions
      const completionStages = [
        'optimizing_for_mesh',
        'generating_3d',
        'complete',
        'completed',
        'done'
      ];

      if (completionStages.includes(stage) || (stage === 'generating_2d' && imageUrl)) {
        this._stopPolling();
        this.imageUrl = imageUrl;
        this.result = {
          imageUrl: imageUrl,
          descriptions: data.descriptions || data.result?.descriptions,
          prompt: data.prompt || data.result?.prompt
        };
        this._setState(CharacterGenerationCard.STATES.COMPLETE);
        this.onComplete(this.result);
      }
    } catch (error) {
      this._stopPolling();
      this._setError(error.message);
    }
  }

  _setError(message) {
    this.errorMessage = message;
    this._setState(CharacterGenerationCard.STATES.ERROR);
    this.onError(new Error(message));
  }

  reset() {
    this._stopPolling();
    this.generationId = null;
    this.imageUrl = null;
    this.result = null;
    this.errorMessage = null;
    this._setState(CharacterGenerationCard.STATES.IDLE);
  }

  destroy() {
    this._stopPolling();
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }

  getElement() {
    return this.element;
  }

  getState() {
    return this.state;
  }

  getResult() {
    return this.result;
  }

  /**
   * Debug method to display a completed character without running generation
   * @param {string} imageUrl - URL of the character image
   */
  debugShowComplete(imageUrl) {
    this.imageUrl = imageUrl;
    this.result = { imageUrl };
    this._setState(CharacterGenerationCard.STATES.COMPLETE);
  }
}
